import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useCurrentCountSession } from '../hooks/use-count-session'
import { useCountRestaurantStore } from '../store/count-restaurant-store'
import { ensureAudioUnlocked } from '../lib/scan-feedback'
import { safeAreaBottom } from '../lib/safe-area'
import { CountSessionMenu } from './count-session-menu'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** How often the elapsed-time line is recomputed — it is displayed in whole minutes. */
const TICK_MS = 30_000

/**
 * Minimal shell for the /inventory/count/* routes: no desktop sidebar, the counted location and
 * the running session time in the header, and a session menu instead of a back arrow.
 *
 * Full height comes from `h-dvh` rather than `h-screen`: on iOS `100vh` is the *largest* possible
 * viewport, so with Safari's chrome visible the bottom of a `h-screen` column sits below the fold
 * and its primary action is unreachable. Bottom padding adds `env(safe-area-inset-bottom)` so the
 * scroll container clears the home indicator under `viewport-fit=cover`.
 *
 * The root deliberately carries no `data-app-chrome`: that attribute marks navigation the print
 * stylesheet hides, and on this root (copied from app-shell.tsx) it hid the entire counting
 * screen when printing.
 */
export function CountMobileLayout() {
  const { t } = useTranslation('inventory-count')
  const navigate = useNavigate()
  const restaurantId = useCountRestaurantStore((s) => s.restaurantId)
  const { data: restaurantsData } = useRestaurants()
  // Same query key as the pages below, so react-query serves this from cache instead of a second
  // request per navigation.
  const { data: current } = useCurrentCountSession(restaurantId)
  const pathname = useLocation({ select: (l) => l.pathname })

  const locationName =
    restaurantsData?.items.find((r) => r.id === restaurantId)?.name ?? null
  const startedAt = current?.session.startedAt ?? null
  // The capture screen owns the whole viewport: the camera is the screen, and the bands below it
  // are pinned to the bottom edge, so the shell must not pad or scroll it.
  const isCapture = pathname === '/inventory/count/capture'
  // Getting back to scanning used to mean pausing the session, landing on the overview and
  // resuming it. The chevron leads deeper into the same count, not out of it, which is why it is
  // safe here and was not on the capture screen (CHG-2026-09-inventory-count-review-ux).
  const isReview = pathname === '/inventory/count/review'

  const [elapsedMinutes, setElapsedMinutes] = useState<number | null>(null)

  useEffect(() => {
    if (!startedAt) {
      setElapsedMinutes(null)
      return
    }
    const startedMs = new Date(startedAt).getTime()
    const tick = () =>
      setElapsedMinutes(
        Math.max(0, Math.floor((Date.now() - startedMs) / 60000)),
      )
    tick()
    const timer = setInterval(tick, TICK_MS)
    return () => clearInterval(timer)
  }, [startedAt])

  return (
    <div
      className="flex h-dvh flex-col bg-background"
      // First touch anywhere in the flow unlocks WebAudio, which iOS only allows from inside a
      // user gesture — otherwise the scan beeps stay silent for the whole session.
      onPointerDown={ensureAudioUnlocked}
    >
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        {isReview && (
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label={t('layout.toCapture')}
            onClick={() => navigate({ to: '/inventory/count/capture' })}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">
            {locationName ?? t('layout.title')}
          </h1>
          {elapsedMinutes != null && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {t('layout.elapsed', { minutes: elapsedMinutes })}
            </p>
          )}
        </div>
        <CountSessionMenu
          restaurantId={restaurantId}
          sessionId={current?.session.id ?? null}
          showReviewAction={isCapture}
          expiresAt={current?.expiresAt ?? null}
        />
      </header>
      <main
        className={cn(
          'min-h-0 flex-1',
          isCapture ? 'overflow-hidden' : 'overflow-y-auto p-4',
        )}
        style={isCapture ? undefined : safeAreaBottom('1rem')}
      >
        <Outlet />
      </main>
    </div>
  )
}
